
import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Stats } from './components/Stats';
import { Services } from './components/Services';
import { Process } from './components/Process';
import { Portfolio } from './components/Portfolio';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { Footer } from './components/Footer';
import { BackgroundAnimation } from './components/BackgroundAnimation';
import { X, Copy, Mail, Users, CheckCircle2 } from 'lucide-react';
import { SITE_CONFIG } from './data';

function App() {
  // Contact Modal State
  const [showContactModal, setShowContactModal] = useState(false);

  // Toast Notification State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    // Welcome message for dev
    console.log("%c Zivush Development ", "background: #14121d; color: #3b82f6; font-weight: bold; padding: 4px; border-radius: 4px;");
    console.log("Content is managed via data.ts");
  }, []);

  const showNotification = (msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification(`Copied ${label} to clipboard!`);
    }).catch(() => {
      showNotification(`Failed to copy ${label}.`);
    });
  };

  // Simple separator component
  const SectionSeparator = () => (
    <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent opacity-70" />
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-mc-blue/30 selection:text-mc-blue relative">
      {/* Global Animated Background */}
      <BackgroundAnimation />

      <Navbar onContactClick={() => {
        setShowContactModal(true);
      }} />
      <main className="relative z-10">
        <Hero config={SITE_CONFIG} onContactClick={() => setShowContactModal(true)} />
        <Stats />
        <Services />
        <Process />
        <SectionSeparator />
        <Portfolio />
        <SectionSeparator />
        <Reviews />
        <FAQ />
      </main>
      
      <Footer config={SITE_CONFIG} />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] bg-slate-900 border border-mc-blue/50 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4">
          <CheckCircle2 className="w-4 h-4 text-mc-blue" />
          <span className="font-medium text-sm">{toastMessage}</span>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl w-full max-w-md relative animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-xl">Contact & Hire</h3>
              <button 
                onClick={() => {
                  setShowContactModal(false);
                }} 
                className="text-slate-500 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Email */}
              <button 
                onClick={() => handleCopy(SITE_CONFIG.email, 'Email')}
                className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl hover:border-mc-blue/50 transition-all group text-left flex items-center gap-4"
              >
                <div className="p-3 bg-slate-900 rounded-lg group-hover:bg-mc-blue/10 group-hover:text-mc-blue transition-colors">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">Email</p>
                  <p className="text-white font-mono">{SITE_CONFIG.email}</p>
                </div>
                <Copy className="w-4 h-4 ml-auto text-slate-600 group-hover:text-white" />
              </button>

              {/* Discord Handle */}
              <button 
                 onClick={() => handleCopy(SITE_CONFIG.discordHandle, 'Discord Handle')}
                 className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl hover:border-[#5865F2]/50 transition-all group text-left flex items-center gap-4"
              >
                <div className="p-3 bg-slate-900 rounded-lg group-hover:bg-[#5865F2]/10 group-hover:text-[#5865F2] transition-colors">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-bold">Discord Handle</p>
                  <p className="text-white font-mono">{SITE_CONFIG.discordHandle}</p>
                </div>
                <Copy className="w-4 h-4 ml-auto text-slate-600 group-hover:text-white" />
              </button>

              {/* Discord Server Link */}
              <a 
                 href={SITE_CONFIG.discordInvite} 
                 target="_blank" 
                 rel="noreferrer"
                 className="w-full p-4 bg-[#5865F2] text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#4752C4] transition-colors shadow-lg"
              >
                <Users className="w-5 h-5" />
                Join Discord Server
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
